/**
 * Created by Yoyo Fang on 12/04/2018.
 * Description:
 * Email Digest component
 *
 * ------ maintenance history ------
 * Update by Marcus Zhao 01/08/2023 Support confirm dialog before send email digetst.
 */

import { Component, OnInit } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { Store } from '@ngrx/store';
import { AppState } from '../../redux';
import { take, catchError } from 'rxjs/operators';
import { NotificationStyles } from '../../widgets/notification/notification.model';
import { ToastService } from '../../widgets/toast/toast.service';
import { UpdateSubscription, EmailDigestActionTypes } from '../../redux/actions/email-digest.actions';
import { Router } from '@angular/router';
import * as lodash from 'lodash';
import { EmailDigest, EmailDigestType } from '../../tamalelibs/models/emaildigest.model';
import { EmailDigestService } from '../../services/email-digest.service';
import { of, Subject } from 'rxjs';
import { AlertWindowService } from '../../widgets/alert-window/alert-window.service';
import { SystemUser } from '../../tamalelibs/models/user.model';
import { StoreQuerierService } from '../../services/store-querier.service';
import { EmailDigestPreviweBoxActions, EmailDigestDialogActions, EmailGridContextMenu } from './email-digest.model';
import { ConfirmSendDialogService } from './confirm-send-dialog/confirm-send-dialog.component.service';
import { ConfirmDialogOptions } from './confirm-send-dialog/confirm-send-dialog.model';

@Component({
    selector: 'email-digest-actions',
    templateUrl: './email-digest-actions.component.html',
    styleUrls: ['./email-digest-actions.component.scss']
})
export class EmailDigestActionsComponent implements ICellRendererAngularComp, OnInit {
    item: EmailDigest;
    currentUser: SystemUser;
    isDashboardType: boolean;

    private _event$: Subject<any>;

    constructor(
        private _toast: ToastService,
        private _store: Store<AppState>,
        private _router: Router,
        private _emailDigestService: EmailDigestService,
        private _alert: AlertWindowService,
        private _storeQuerier: StoreQuerierService,
        private _confirmSendDialogService: ConfirmSendDialogService,
    ) { }
    agInit(params: any): void {
        this.item = params.data;
        this.isDashboardType = this.item.type === EmailDigestType.DASHBOARD;
        this._event$ = params.event$;
        // id
        // isSubscribable: true
        // isSubscribed: true
        // editable
        // deletable
    }
    ngOnInit() {
        this.currentUser = this._storeQuerier.getCurrentUser();
    }

    preview(event) {
        if (this.isDashboardType) {
            return;
        }
        this._emailDigestService.previewActionSubject$.next({
            type: EmailDigestPreviweBoxActions.PREVIEW,
            payload: {
                event: event,
                data: this._emailDigestService.addConfigurationTimezone(this.item),
                open: true,
            },
        });
    }

    switchSubscription(e) {
        const emailDigestItem = {
            id: this.item.id,
            isSubscribed: this.item.isSubscribed
        };
        this._store.dispatch(new UpdateSubscription(emailDigestItem));
    }

    private _initContextItems() {
        const items = [];
        if (this.item.editable) {
            items.push({
                id: '0',
                text: 'Edit',
                event: () => this._onMenuSelect(0)
            });
        }
        // PPC or author can copy
        if (this.currentUser.credential.publishPubConfiguration || this.item.author.id === this.currentUser.id) {
            items.push({
                id: '1',
                text: 'Copy',
                event: () => this._onMenuSelect(1)
            });
        }
        if (this.currentUser.credential.publishPubConfiguration) {
            items.push({
                id: '4',
                text: this.item.active ? 'Deactivate' : 'Activate',
                event: () => this._onMenuSelect(4)
            });
        }
        if (this.item.deletable) {
            items.push({
                id: '2',
                text: 'Delete',
                event: () => this._onMenuSelect(2)
            });
        }
        if (items.length > 0) {
            items.push({
                divider: true
            });
        }
        items.push({
            id: '3',
            text: 'Send Now',
            event: () => this._onMenuSelect(3)
        });
        return items;
    }

    private _onMenuSelect(type) {
        switch (type) {
            case 0:
                // edit
                if (this.isDashboardType) {
                    this._event$.next({
                        type: EmailDigestDialogActions.EDIT,
                        payload: this.item
                    });
                } else {
                    this._store.dispatch({
                        type: EmailDigestActionTypes.SET_CONFIG,
                        payload: lodash.cloneDeep(this.item),
                    });
                    this._router.navigate(['email-digest', this.item.id]);
                }
                break;
            case 1:
                // copy
                const temp = lodash.cloneDeep(this.item);
                delete temp.id;
                if (this.isDashboardType) {
                    this._event$.next({
                        type: EmailDigestDialogActions.COPY,
                        payload: temp
                    });
                } else {
                    this._store.dispatch({
                        type: EmailDigestActionTypes.SET_CONFIG,
                        payload: temp,
                    });
                    this._router.navigate(['email-digest/copy', this.item.id]);
                }
                break;
            case 2:
                // delete
                this._alert.warn('Delete confirmation required', ['Are you sure you want to delete ' + this.item.name + '?'], 'Delete').pipe(
                    take(1),
                ).subscribe(result => {
                    if (result) {
                        this._emailDigestService.deleteEmailDigest(this.item.id).pipe(
                            catchError((e: any) => {
                                this._toast.notify({
                                    style: NotificationStyles.Error,
                                    message: 'Delete failed, error: ' + e.message,
                                });
                                return of(null);
                            }),
                            take(1),
                        ).subscribe(data => {
                            if (data) {
                                this._toast.notify({
                                    style: NotificationStyles.Success,
                                    message: data.name + ' successfully deleted',
                                });
                                // Delete from list
                                this._store.dispatch({
                                    type: EmailDigestActionTypes.DELETE_CONFIG,
                                    payload: this.item,
                                });
                            }
                        });
                    }
                });
                break;
            case 3:
                this._confirmSendDialogService.dialogOpen$.next(this.item);
                break;
            case 4:
                // de/activate
                this._store.dispatch({
                    type: EmailDigestActionTypes.SWITCH_ACTIVE,
                    payload: {
                        id: this.item.id,
                        active: !this.item.active,
                    },
                });
                break;
            default:
                break;
        }
    }

    showContextMenu(e: MouseEvent) {
        e.stopPropagation();
        const items = this._initContextItems();
        this._event$.next({
            type: EmailGridContextMenu.SHOW,
            payload: {
                target: e.target,
                data: items
            }
        });
    }

    refresh(): boolean {
        return true;
    }
}
